import { Injectable } from "@angular/core";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import {
  StudentPortalBrandingApiService,
  StudentPortalBranding,
  StudentPortalBrandingFields,
} from "../../services/student-portal-branding.service";

export const STUDENT_PORTAL_BRANDING_CACHE_KEY = "saferoads-student-portal-branding-v1";

/** Localized view of branding for the active UI language. */
export interface LocalizedStudentBranding {
  showBrandText: boolean;
  programTitle: string;
  programSubtitle: string;
  tagline: string;
  footerDescription: string;
  contactEmail: string;
  contactPhone: string;
  contactAddress: string;
  officeHours: string;
  contactNote: string;
  aboutLinkName: string;
  homeTitle: string;
  homeSubtitle: string;
  showHomeTagline: boolean;
  footerBackground: string;
  coursesTitle: string;
  coursesSubtitle: string;
  contactPageTitle: string;
  contactPageSubtitle: string;
}

export function emptyStudentPortalBranding(): StudentPortalBrandingFields {
  return {
    showBrandText: true,
    programTitleEn: "",
    programTitleEs: "",
    programSubtitleEn: "",
    programSubtitleEs: "",
    taglineEn: "",
    taglineEs: "",
    footerDescriptionEn: "",
    footerDescriptionEs: "",
    contactEmail: "",
    contactPhone: "",
    contactAddress: "",
    officeHoursEn: "",
    officeHoursEs: "",
    contactNoteEn: "",
    contactNoteEs: "",
    aboutLinkNameEn: "",
    aboutLinkNameEs: "",
    homeTitleEn: "",
    homeTitleEs: "",
    homeSubtitleEn: "",
    homeSubtitleEs: "",
    showHomeTagline: true,
    footerBackground: "primary",
    coursesTitleEn: "",
    coursesTitleEs: "",
    coursesSubtitleEn: "",
    coursesSubtitleEs: "",
    contactPageTitleEn: "",
    contactPageTitleEs: "",
    contactPageSubtitleEn: "",
    contactPageSubtitleEs: "",
  };
}

function normalizeBranding(
  branding: StudentPortalBranding | StudentPortalBrandingFields
): StudentPortalBrandingFields {
  return {
    showBrandText: branding.showBrandText ?? true,
    programTitleEn: branding.programTitleEn ?? "",
    programTitleEs: branding.programTitleEs ?? "",
    programSubtitleEn: branding.programSubtitleEn ?? "",
    programSubtitleEs: branding.programSubtitleEs ?? "",
    taglineEn: branding.taglineEn ?? "",
    taglineEs: branding.taglineEs ?? "",
    footerDescriptionEn: branding.footerDescriptionEn ?? "",
    footerDescriptionEs: branding.footerDescriptionEs ?? "",
    contactEmail: branding.contactEmail ?? "",
    contactPhone: branding.contactPhone ?? "",
    contactAddress: branding.contactAddress ?? "",
    officeHoursEn: branding.officeHoursEn ?? "",
    officeHoursEs: branding.officeHoursEs ?? "",
    contactNoteEn: branding.contactNoteEn ?? "",
    contactNoteEs: branding.contactNoteEs ?? "",
    aboutLinkNameEn: branding.aboutLinkNameEn ?? "",
    aboutLinkNameEs: branding.aboutLinkNameEs ?? "",
    homeTitleEn: branding.homeTitleEn ?? "",
    homeTitleEs: branding.homeTitleEs ?? "",
    homeSubtitleEn: branding.homeSubtitleEn ?? "",
    homeSubtitleEs: branding.homeSubtitleEs ?? "",
    showHomeTagline: branding.showHomeTagline ?? true,
    footerBackground: (branding.footerBackground ?? "primary").trim() || "primary",
    coursesTitleEn: branding.coursesTitleEn ?? "",
    coursesTitleEs: branding.coursesTitleEs ?? "",
    coursesSubtitleEn: branding.coursesSubtitleEn ?? "",
    coursesSubtitleEs: branding.coursesSubtitleEs ?? "",
    contactPageTitleEn: branding.contactPageTitleEn ?? "",
    contactPageTitleEs: branding.contactPageTitleEs ?? "",
    contactPageSubtitleEn: branding.contactPageSubtitleEn ?? "",
    contactPageSubtitleEs: branding.contactPageSubtitleEs ?? "",
  };
}

export function localizeStudentBranding(
  branding: StudentPortalBrandingFields,
  lang: string
): LocalizedStudentBranding {
  const es = (lang || "en").toLowerCase().startsWith("es");
  const pick = (en: string, esVal: string) => (es ? esVal : en);
  return {
    showBrandText: branding.showBrandText,
    programTitle: pick(branding.programTitleEn, branding.programTitleEs),
    programSubtitle: pick(branding.programSubtitleEn, branding.programSubtitleEs),
    tagline: pick(branding.taglineEn, branding.taglineEs),
    footerDescription: pick(branding.footerDescriptionEn, branding.footerDescriptionEs),
    contactEmail: branding.contactEmail,
    contactPhone: branding.contactPhone,
    contactAddress: branding.contactAddress,
    officeHours: pick(branding.officeHoursEn, branding.officeHoursEs),
    contactNote: pick(branding.contactNoteEn, branding.contactNoteEs),
    aboutLinkName: pick(branding.aboutLinkNameEn, branding.aboutLinkNameEs),
    homeTitle: pick(branding.homeTitleEn, branding.homeTitleEs),
    homeSubtitle: pick(branding.homeSubtitleEn, branding.homeSubtitleEs),
    showHomeTagline: branding.showHomeTagline,
    footerBackground: branding.footerBackground || "primary",
    coursesTitle: pick(branding.coursesTitleEn, branding.coursesTitleEs),
    coursesSubtitle: pick(branding.coursesSubtitleEn, branding.coursesSubtitleEs),
    contactPageTitle: pick(branding.contactPageTitleEn, branding.contactPageTitleEs),
    contactPageSubtitle: pick(
      branding.contactPageSubtitleEn,
      branding.contactPageSubtitleEs
    ),
  };
}

export function readCachedStudentPortalBranding(): StudentPortalBrandingFields | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  try {
    const raw = localStorage.getItem(STUDENT_PORTAL_BRANDING_CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }
    return normalizeBranding(parsed as StudentPortalBrandingFields);
  } catch {
    return null;
  }
}

export function writeCachedStudentPortalBranding(
  branding: StudentPortalBrandingFields
): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  try {
    localStorage.setItem(STUDENT_PORTAL_BRANDING_CACHE_KEY, JSON.stringify(branding));
  } catch {
    // ignore quota / private mode
  }
}

@Injectable({
  providedIn: "root",
})
export class BrandingService {
  private readonly brandingSubject = new BehaviorSubject<StudentPortalBrandingFields>(
    readCachedStudentPortalBranding() ?? emptyStudentPortalBranding()
  );

  readonly branding$ = this.brandingSubject.asObservable();

  get current(): StudentPortalBrandingFields {
    return this.brandingSubject.value;
  }

  constructor(private api: StudentPortalBrandingApiService) {}

  applyBranding(
    branding: StudentPortalBranding | StudentPortalBrandingFields | null | undefined
  ): void {
    if (!branding) {
      this.brandingSubject.next(emptyStudentPortalBranding());
      return;
    }
    const next = normalizeBranding(branding);
    this.brandingSubject.next(next);
    writeCachedStudentPortalBranding(next);
  }

  async loadAndApply(): Promise<void> {
    try {
      const branding = await firstValueFrom(this.api.getActive());
      this.applyBranding(branding);
    } catch (error) {
      console.warn(
        "Student portal branding unavailable; keeping cached branding if present.",
        error
      );
    }
  }
}
